import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Calendar, Check, Medal, Shield, Home, Heart, HelpCircle, X } from "lucide-react";
import { ParticleCanvas } from "./ParticleCanvas";
import { RegistrationForm } from "./RegistrationForm";
import { Reveal, SectionLabel, ParallaxBg, Countdown } from "./Chrome";
import { HOST, WEBINAR, FALSE_BELIEFS, AUDIENCE_CARDS, ROADMAP, OSCAR_URL, HOUSE_BG_URL, VA_BG_URL, scrollToId } from "../content";

gsap.registerPlugin(ScrollTrigger);

const ICONS = { Medal, Shield, Home, Heart, HelpCircle };

const heroLine = {
  hidden: { y: "110%" },
  show: (i) => ({ y: 0, transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.16, 1, 0.3, 1] } }),
};

export const Hero = () => {
  const [assembled, setAssembled] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setAssembled(true), 1900);
    return () => clearTimeout(t);
  }, []);

  return (
    <section id="hero" className="relative min-h-screen grid grid-cols-1 lg:grid-cols-2 items-center gap-10 px-6 md:px-12 xl:px-20 pt-28 pb-16 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <img src={HOUSE_BG_URL} alt="" className="w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to right, rgba(7,11,17,0.55) 0%, rgba(7,11,17,0.72) 45%, rgba(7,11,17,0.94) 68%)" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(7,11,17,0.8) 0%, transparent 32%, rgba(7,11,17,0.55) 100%)" }} />
      </div>
      <div className="absolute inset-0">
        <ParticleCanvas className="absolute inset-0" />
        {!assembled && <div className="hero-shimmer" />}
      </div>

      <div className="relative z-10 max-w-2xl">
        <p className="font-mono-c text-[10px] tracking-[0.3em] text-white/30 flex items-center gap-2 mb-8" data-testid="hero-date">
          <Calendar className="w-3 h-3" /> {WEBINAR.date}
        </p>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="font-mono-c text-[10px] tracking-[0.28em] text-[#5D8FC4] uppercase"
          data-testid="hero-eyebrow"
        >
          {WEBINAR.eyebrow}
        </motion.p>
        <h1 className="mt-5 uppercase font-black tracking-[-0.03em] leading-[0.95] text-[clamp(40px,5.4vw,76px)]" data-testid="hero-h1">
          <span className="block overflow-hidden"><motion.span custom={0} variants={heroLine} initial="hidden" animate="show" className="block">Know Your Exact VA</motion.span></span>
          <span className="block overflow-hidden"><motion.span custom={1} variants={heroLine} initial="hidden" animate="show" className="block text-[#5D8FC4]">Purchasing Power—</motion.span></span>
          <span className="block overflow-hidden"><motion.span custom={2} variants={heroLine} initial="hidden" animate="show" className="block opacity-60 text-[62%]">Before You Talk to a Single Lender</motion.span></span>
        </h1>
        <p className="mt-6 text-[15px] text-white/55 leading-relaxed max-w-xl" data-testid="hero-subheadline">
          The free 45-minute live session that shows <span className="text-[#5D8FC4]">veterans</span> and{" "}
          <span className="text-[#5D8FC4]">active duty service members</span> their exact $0-down purchasing power —
          before getting quoted like a conventional buyer or shopped around to four lenders at once.
        </p>
        <blockquote className="mt-6 border-l-2 border-[#5D8FC4] pl-4 italic text-sm text-white/70 max-w-lg" data-testid="hero-blockquote">
          What if the down payment you've been told you "need" isn't actually required by your VA benefit at all?
        </blockquote>
        <div className="mt-8">
          <p className="font-mono-c text-[10px] tracking-[0.3em] text-white/35 uppercase mb-3">The Live Briefing Starts In:</p>
          <Countdown />
        </div>
        <div className="mt-8">
          <button className="btn-accent text-sm flex items-center gap-2" data-testid="hero-cta-button" onClick={() => scrollToId("register")}>
            <Check className="w-4 h-4" strokeWidth={3} /> Reserve My Free Seat
          </button>
          <p className="font-mono-c text-[11px] text-white/25 mt-3 tracking-widest uppercase">Low Ticket Notice! — Seats fill fast</p>
        </div>
        <div className="mt-12 flex items-center gap-4" data-testid="hero-host-module">
          <img src={OSCAR_URL} alt={HOST.name} className="w-14 h-14 rounded-full border-2 border-[#5D8FC4] object-cover" />
          <div>
            <p className="text-sm font-bold">{HOST.name}</p>
            <p className="text-xs text-white/45">{HOST.company} · {HOST.nmls}</p>
          </div>
        </div>
      </div>

      <div className="relative z-10 w-full max-w-md lg:justify-self-end float-slow">
        <Reveal delay={0.3}>
          <RegistrationForm idPrefix="hero" showBonuses />
        </Reveal>
      </div>
    </section>
  );
};

const TICKER_ITEMS = [
  "45 MINUTES", "5 CORE MODULES", "LIVE Q&A INCLUDED", "FLORIDA COVERAGE",
  "NO COST TO ATTEND", "VA HOME LOAN & $0-DOWN SPECIALISTS",
];

export const Ticker = () => (
  <div className="border-y border-white/10 py-4 overflow-hidden relative" data-testid="proof-ticker">
    <div className="marquee-track">
      {[0, 1].map((rep) => (
        <div key={rep} className="flex shrink-0">
          {TICKER_ITEMS.map((item, i) => (
            <span key={i} className="font-mono-c text-[11px] tracking-[0.25em] text-white/40 flex items-center whitespace-nowrap">
              <span className="px-6">{item}</span>
              <span className="text-[#5D8FC4]">◆</span>
            </span>
          ))}
        </div>
      ))}
    </div>
  </div>
);

const MANIFESTO_LINES = [
  { text: "VA HOME LOANS", cls: "text-[#EDF2F7]" },
  { text: "SHOULD NOT FEEL", cls: "text-white/[0.28]" },
  { text: "LIKE DECODING", cls: "text-[#EDF2F7]" },
  { text: "FINE PRINT.", cls: "text-[#5D8FC4]" },
];

export const Manifesto = () => {
  const ref = useRef(null);
  useEffect(() => {
    const words = ref.current.querySelectorAll(".m-word");
    const anim = gsap.fromTo(words, { opacity: 0, y: 30 }, {
      opacity: 1, y: 0, stagger: 0.04, duration: 0.7, ease: "power3.out",
      scrollTrigger: { trigger: ref.current, start: "top 75%" },
    });
    return () => { anim.scrollTrigger && anim.scrollTrigger.kill(); anim.kill(); };
  }, []);

  return (
    <section className="relative px-6 md:px-12 xl:px-20 py-28 md:py-40 overflow-hidden" data-testid="manifesto-section">
      <ParallaxBg opacity={0.22} />
      <div className="relative z-10 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-12 items-start">
        <div>
          <SectionLabel num="001" text="The Problem" />
          <h2 ref={ref} className="mt-8 uppercase font-black tracking-[-0.02em] leading-[0.97] text-[clamp(36px,5vw,68px)]">
            {MANIFESTO_LINES.map((line, li) => (
              <span key={li} className={`block ${line.cls}`}>
                {line.text.split(" ").map((w, wi) => (
                  <span key={wi} className="m-word inline-block mr-[0.22em]">{w}</span>
                ))}
              </span>
            ))}
          </h2>
          <Reveal delay={0.2}>
            <p className="mt-10 text-white/55 text-base leading-[1.75] max-w-[560px]">
              You earned this benefit through service — yet most veterans and active duty buyers are handed conventional-loan
              math, vague answers about entitlement, and four competing quotes that never explain what the VA guarantee
              actually does. This 45-minute briefing strips out the jargon and shows you, step by step, how your $0-down
              purchasing power is really calculated — so the fine print stops being fine print.
            </p>
            <button className="btn-outline-accent text-sm mt-8" data-testid="manifesto-cta" onClick={() => scrollToId("register")}>
              Reserve My Free Seat
            </button>
          </Reveal>
        </div>
        <div className="hidden lg:grid grid-cols-8 gap-2 mt-24" aria-hidden="true">
          {Array.from({ length: 64 }).map((_, i) => (
            <span key={i} className="w-3 h-3 bg-[#5D8FC4] opacity-[0.06]" />
          ))}
        </div>
      </div>
    </section>
  );
};

export const Letter = () => (
  <section className="relative bg-white/[0.015] py-28 md:py-36 px-6 overflow-hidden" data-testid="letter-section">
    <ParallaxBg opacity={0.15} src={VA_BG_URL} />
    <div className="relative z-10 max-w-[760px] mx-auto">
      <SectionLabel num="002" text="A Letter From The Host" />
      <Reveal>
        <p className="mt-10 text-xl text-white/60 italic">Fellow Veteran,</p>
        <p className="mt-6 text-white/55 leading-[1.8]">
          If you've started looking into buying a home, you've probably already run into some version of these three ideas:
        </p>
      </Reveal>
      <div className="mt-8 space-y-4">
        {FALSE_BELIEFS.map((belief, i) => (
          <Reveal key={i} delay={i * 0.1}>
            <div className="flex items-start gap-4 p-5 bg-red-500/[0.04] border border-red-500/10" data-testid={`false-belief-card-${i + 1}`}>
              <X className="w-5 h-5 text-red-400 shrink-0 mt-0.5" strokeWidth={2.5} />
              <p className="text-white/75 italic leading-relaxed">{belief}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <div className="h-px bg-[#5D8FC4]/40 my-12" />
      <Reveal>
        <div className="space-y-6 text-white/55 leading-[1.8]">
          <p>
            Here's what years of working inside the VA loan process have taught me: none of those beliefs hold up. The VA
            guarantee was built specifically so eligible veterans and service members can purchase with $0 down — the
            "required down payment" you've been told about often isn't a VA requirement at all.
          </p>
          <p>
            And lenders are not interchangeable. A lender who works with VA files every day reads entitlement, funding fee
            exemptions, and residual income very differently from one who mostly closes conventional loans — and that
            difference shows up in what you're quoted and how smoothly things move.
          </p>
          <p>
            As for waiting until things feel "more stable" — understanding your position costs nothing and requires no hard
            credit pull. Knowing your number early is what creates stability, because you can plan around facts instead of
            guesses. That's exactly what this briefing is designed to give you.
          </p>
        </div>
        <p className="mt-10 text-[#5D8FC4] font-bold">— {HOST.name}, {HOST.company}</p>
      </Reveal>
    </div>
  </section>
);

export const Audience = () => (
  <section id="learn" className="px-6 md:px-12 xl:px-20 py-28 md:py-36" data-testid="audience-section">
    <SectionLabel num="003" text="Who This Is For" />
    <Reveal>
      <h2 className="mt-6 uppercase font-black tracking-tight text-3xl md:text-5xl">This Training Is Designed For:</h2>
    </Reveal>
    <div className="mt-12 grid grid-cols-2 lg:grid-cols-5 gap-4">
      {AUDIENCE_CARDS.map((card, i) => {
        const Icon = ICONS[card.icon];
        return (
          <Reveal key={i} delay={i * 0.08}>
            <div className="audience-card p-6 h-full flex flex-col gap-4" data-testid={`audience-card-${i + 1}`}>
              <Icon className="w-7 h-7 text-[#5D8FC4]" strokeWidth={1.5} />
              <p className="text-sm font-semibold leading-snug">{card.text}</p>
            </div>
          </Reveal>
        );
      })}
    </div>
    <Reveal delay={0.2}>
      <button className="btn-accent text-sm mt-12 flex items-center gap-2" data-testid="audience-cta" onClick={() => scrollToId("register")}>
        <Check className="w-4 h-4" strokeWidth={3} /> Reserve My Free Seat
      </button>
    </Reveal>
  </section>
);

export const Roadmap = () => (
  <section className="relative border-y border-white/10 overflow-hidden" data-testid="roadmap-section">
    <ParallaxBg opacity={0.14} />
    <div className="relative z-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
      {ROADMAP.map((item, i) => (
        <div key={i} className={`roadmap-cell relative p-10 overflow-hidden ${i > 0 ? "border-t sm:border-t-0 sm:border-l border-white/10" : ""}`} data-testid={`roadmap-step-${i + 1}`}>
          <span className="absolute -top-6 -right-2 font-mono-c text-[140px] font-bold text-white/[0.04] leading-none select-none">{item.step}</span>
          <Reveal delay={i * 0.1}>
            <p className="font-mono-c text-[11px] tracking-[0.3em] text-[#5D8FC4]">STEP {item.step}</p>
            <h3 className="mt-4 text-xl font-black uppercase">{item.name}</h3>
            <p className="mt-3 text-sm text-white/50 leading-relaxed">{item.desc}</p>
          </Reveal>
        </div>
      ))}
    </div>
  </section>
);
